import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BACKEND_URL } from '../config/canonical';
import {
  FileText, Search, RefreshCw, Plus, User, Clock, CheckCircle, Send, Trash2, Download,
  Eye, X, Edit2, Printer, MapPin, Car, Users, AlertTriangle, Calendar, Building,
  ChevronRight, FileImage, MessageSquare, Scale
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const STATUS_COLORS = {
  DRAFT: 'bg-slate-500', SUBMITTED: 'bg-blue-500', APPROVED: 'bg-green-500', REJECTED: 'bg-red-500'
};

const Reports = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('nexus_token');
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('ALL');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewReport, setViewReport] = useState(null);
  const [reportDetail, setReportDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const isAdmin = user?.role === 'department_admin' || user?.role === 'main_hub';

  const fetchCases = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCases(await res.json());
    } catch (e) { console.error(e); }
  }, [token]);

  const fetchReports = useCallback(async () => {
    if (selectedCase === 'ALL' || !selectedCase) {
      const allReports = [];
      for (const c of cases) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/cases/${c.id}/reports`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const reps = await res.json();
            allReports.push(...reps.map(r => ({ ...r, case_title: c.title, case_number: c.case_number })));
          }
        } catch (e) { console.error(e); }
      }
      setReports(allReports.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cases/${selectedCase}/reports`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const reps = await res.json();
        const c = cases.find(x => x.id === selectedCase);
        setReports(reps.map(r => ({ ...r, case_title: c?.title, case_number: c?.case_number })));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, selectedCase, cases]);

  useEffect(() => { fetchCases(); }, [fetchCases]);
  useEffect(() => { if (cases.length > 0) fetchReports(); else setLoading(false); }, [fetchReports, cases]);

  const fetchReportDetail = async (report) => {
    setViewReport(report);
    setLoadingDetail(true);
    try {
      // Fetch full report details including related data
      const headers = { Authorization: `Bearer ${token}` };
      const [reportRes, caseRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/reports/${report.id}`, { headers }),
        fetch(`${BACKEND_URL}/api/cases/${report.case_id}`, { headers })
      ]);
      
      const fullReport = reportRes.ok ? await reportRes.json() : report;
      const caseData = caseRes.ok ? await caseRes.json() : null;
      
      // Fetch related data from case
      let subjects = [], vehicles = [], evidence = [], locations = [], interviews = [];
      if (caseData) {
        const [subjRes, vehRes, evRes, locRes, intRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/cases/${report.case_id}/subjects`, { headers }),
          fetch(`${BACKEND_URL}/api/cases/${report.case_id}/vehicles`, { headers }),
          fetch(`${BACKEND_URL}/api/cases/${report.case_id}/evidence`, { headers }),
          fetch(`${BACKEND_URL}/api/locations?case_id=${report.case_id}`, { headers }),
          fetch(`${BACKEND_URL}/api/cases/${report.case_id}/interviews`, { headers })
        ]);
        subjects = subjRes.ok ? await subjRes.json() : [];
        vehicles = vehRes.ok ? await vehRes.json() : [];
        evidence = evRes.ok ? await evRes.json() : [];
        locations = locRes.ok ? await locRes.json() : [];
        interviews = intRes.ok ? await intRes.json() : [];
      }
      
      setReportDetail({
        ...fullReport,
        case: caseData,
        subjects,
        vehicles,
        evidence,
        locations,
        interviews
      });
    } catch (e) {
      console.error(e);
      setReportDetail(report);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSubmit = async (reportId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/${reportId}/submit`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast({ title: 'Success', description: 'Report submitted for approval' }); fetchReports(); setViewReport(null); }
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
  };

  const handleApprove = async (reportId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/${reportId}/approve`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast({ title: 'Success', description: 'Report approved' }); fetchReports(); setViewReport(null); }
    } catch (e) { toast({ title: 'Error', description: e.message, variant: 'destructive' }); }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    await fetch(`${BACKEND_URL}/api/reports/${reportId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchReports();
    setViewReport(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const filtered = reports.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return r.title?.toLowerCase().includes(s) || r.report_type?.toLowerCase().includes(s) || r.investigator_name?.toLowerCase().includes(s);
  });

  const DetailSection = ({ title, icon: Icon, children, count }) => (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-700">
        {Icon && <Icon className="w-5 h-5 text-blue-400" />}
        <h4 className="text-sm font-semibold text-slate-200">{title}</h4>
        {count > 0 && <Badge variant="outline" className="ml-auto text-xs">{count}</Badge>}
      </div>
      {children}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="nx-eyebrow">Documentation</div>
          <h1 className="nx-h1 mt-1">Reports</h1>
          <p className="nx-body mt-1">Investigation reports and findings</p>
        </div>
        {selectedCase && selectedCase !== 'ALL' && (
          <Link to={`/reports/create/${selectedCase}`}>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-report-btn">
              <Plus className="w-4 h-4 mr-2" /> New Report
            </Button>
          </Link>
        )}
      </div>

      <Card className="bg-slate-800/60 border-slate-700">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select value={selectedCase} onValueChange={setSelectedCase}>
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="report-case-select">
                <SelectValue placeholder="All Cases" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Cases</SelectItem>
                {cases.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.case_number} - {c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white pl-10" placeholder="Search reports..." />
            </div>
            <Button variant="outline" className="border-slate-600 text-slate-300" onClick={fetchReports}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading reports...</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-slate-800/60 border-slate-700">
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-slate-600 mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Reports Found</h3>
            <p className="text-slate-400">{search ? 'Try a different search' : 'Create a report from a case detail page'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(report => (
            <Card key={report.id} className="bg-slate-800/60 border-slate-700 hover:border-blue-500/30 transition-colors" data-testid={`report-card-${report.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge variant="outline" className="border-slate-600 text-slate-300 text-xs">{report.report_type?.replace(/_/g, ' ')}</Badge>
                      <Badge className={`${STATUS_COLORS[report.status] || 'bg-slate-500'} text-white text-xs`}>{report.status}</Badge>
                    </div>
                    <h3 className="text-white font-medium">{report.title}</h3>
                    {report.case_number && (
                      <p className="text-xs text-slate-500 mt-1">
                        Case: {report.case_number} - {report.case_title}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" /> {report.investigator_name}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(report.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {report.status === 'DRAFT' && (
                      <Button size="sm" variant="outline" className="border-blue-500 text-blue-400 hover:bg-blue-500/10" onClick={() => handleSubmit(report.id)} data-testid={`submit-report-${report.id}`}>
                        <Send className="w-3 h-3 mr-1" /> Submit
                      </Button>
                    )}
                    {report.status === 'SUBMITTED' && isAdmin && (
                      <Button size="sm" variant="outline" className="border-green-500 text-green-400 hover:bg-green-500/10" onClick={() => handleApprove(report.id)} data-testid={`approve-report-${report.id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Approve
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700" onClick={() => fetchReportDetail(report)} data-testid={`view-report-${report.id}`}>
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                    <a href={`${BACKEND_URL}/api/reports/${report.id}/export-pdf`} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10" data-testid={`export-pdf-${report.id}`}>
                        <Download className="w-3 h-3 mr-1" /> PDF
                      </Button>
                    </a>
                    {isAdmin && (
                      <button onClick={() => handleDelete(report.id)} className="text-slate-500 hover:text-red-400 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Full Report Viewer Modal */}
      {viewReport && (
        <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 overflow-y-auto py-4 print:bg-white print:p-0">
          <div className="bg-slate-900 rounded-lg border border-slate-700 w-full max-w-4xl mx-4 my-auto print:border-0 print:shadow-none print:max-w-none" data-testid="report-detail-modal">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-4 border-b border-slate-700 print:hidden gap-3">
              <div>
                <h3 className="text-lg font-semibold text-white">Report Detail View</h3>
                <p className="text-sm text-slate-400">{viewReport.id}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePrint} className="border-slate-600">
                  <Printer className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Print</span>
                </Button>
                <a href={`${BACKEND_URL}/api/reports/${viewReport.id}/export-pdf`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="border-amber-500 text-amber-400">
                    <Download className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">PDF</span>
                  </Button>
                </a>
                <Link to={`/reports/create/${viewReport.case_id}?edit=${viewReport.id}`}>
                  <Button variant="outline" size="sm" className="border-blue-500 text-blue-400">
                    <Edit2 className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Edit</span>
                  </Button>
                </Link>
                <button onClick={() => { setViewReport(null); setReportDetail(null); }} className="text-slate-400 hover:text-white ml-auto sm:ml-2">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4 sm:p-6 max-h-[80vh] overflow-y-auto print:max-h-none print:overflow-visible">
              {loadingDetail ? (
                <div className="text-center py-12 text-slate-400">Loading report details...</div>
              ) : reportDetail ? (
                <div className="space-y-6">
                  {/* Report Header */}
                  <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="border-slate-600 text-slate-300">{reportDetail.report_type?.replace(/_/g, ' ') || 'Investigation Report'}</Badge>
                          <Badge className={`${STATUS_COLORS[reportDetail.status] || 'bg-slate-500'} text-white`}>{reportDetail.status}</Badge>
                        </div>
                        <h2 className="text-2xl font-bold text-white">{reportDetail.title}</h2>
                      </div>
                      <div className="text-right text-sm text-slate-400">
                        <p>Report ID: <span className="text-white">{reportDetail.id}</span></p>
                        <p>Created: <span className="text-white">{new Date(reportDetail.created_at).toLocaleDateString()}</span></p>
                        {reportDetail.updated_at && (
                          <p>Updated: <span className="text-white">{new Date(reportDetail.updated_at).toLocaleDateString()}</span></p>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="bg-slate-700/50 rounded p-3">
                        <p className="text-slate-400 text-xs">Investigator</p>
                        <p className="text-white flex items-center gap-1"><User className="w-4 h-4" /> {reportDetail.investigator_name || 'N/A'}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded p-3">
                        <p className="text-slate-400 text-xs">Case Number</p>
                        <p className="text-white">{reportDetail.case?.case_number || viewReport.case_number || 'N/A'}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded p-3">
                        <p className="text-slate-400 text-xs">Case Type</p>
                        <p className="text-white">{reportDetail.case?.case_type?.replace(/_/g, ' ') || 'N/A'}</p>
                      </div>
                      <div className="bg-slate-700/50 rounded p-3">
                        <p className="text-slate-400 text-xs">Priority</p>
                        <p className="text-white">{reportDetail.case?.priority || 'NORMAL'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Case Information */}
                  {reportDetail.case && (
                    <DetailSection title="CASE INFORMATION" icon={Building}>
                      <div className="bg-slate-800/30 rounded-lg p-4">
                        <h4 className="text-white font-medium mb-2">{reportDetail.case.title}</h4>
                        {reportDetail.case.description && (
                          <p className="text-slate-400 text-sm">{reportDetail.case.description}</p>
                        )}
                      </div>
                    </DetailSection>
                  )}

                  {/* Report Narrative */}
                  <DetailSection title="REPORT NARRATIVE" icon={FileText}>
                    <div className="bg-slate-800/30 rounded-lg p-4">
                      {reportDetail.narrative ? (
                        <div className="text-slate-300 whitespace-pre-wrap prose prose-invert max-w-none" 
                             dangerouslySetInnerHTML={{ __html: reportDetail.narrative.replace(/<[^>]*>/g, '') }} />
                      ) : (
                        <p className="text-slate-500 italic">No narrative provided.</p>
                      )}
                    </div>
                  </DetailSection>

                  {/* Template Data */}
                  {reportDetail.template_data && Object.keys(reportDetail.template_data).length > 0 && (
                    <DetailSection title="INVESTIGATION DETAILS" icon={AlertTriangle}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(reportDetail.template_data).map(([key, value]) => value && (
                          <div key={key} className="bg-slate-800/30 rounded p-3">
                            <p className="text-slate-400 text-xs mb-1">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                            <p className="text-white text-sm">{value}</p>
                          </div>
                        ))}
                      </div>
                    </DetailSection>
                  )}

                  {/* Subjects */}
                  {reportDetail.subjects && reportDetail.subjects.length > 0 && (
                    <DetailSection title="SUBJECTS OF INTEREST" icon={Users} count={reportDetail.subjects.length}>
                      <div className="space-y-3">
                        {reportDetail.subjects.map((subj, idx) => (
                          <div key={subj.id || idx} className="bg-slate-800/30 rounded-lg p-4 border border-slate-700">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="text-white font-medium">{subj.full_name || 'Unknown Subject'}</h5>
                              {subj.surveillance_risk_level && (
                                <Badge variant="outline" className={`
                                  ${subj.surveillance_risk_level === 'HIGH' ? 'border-red-500 text-red-400' : 
                                    subj.surveillance_risk_level === 'MEDIUM' ? 'border-yellow-500 text-yellow-400' : 
                                    'border-green-500 text-green-400'}
                                `}>
                                  {subj.surveillance_risk_level} Risk
                                </Badge>
                              )}
                            </div>
                            {subj.aliases && subj.aliases.length > 0 && (
                              <p className="text-slate-400 text-sm">Aliases: {subj.aliases.join(', ')}</p>
                            )}
                            {subj.physical_description && (
                              <p className="text-slate-400 text-sm mt-1">{subj.physical_description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </DetailSection>
                  )}

                  {/* Vehicles */}
                  {reportDetail.vehicles && reportDetail.vehicles.length > 0 && (
                    <DetailSection title="VEHICLES" icon={Car} count={reportDetail.vehicles.length}>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-800/50">
                              <th className="text-left px-3 py-2 text-slate-400">Plate</th>
                              <th className="text-left px-3 py-2 text-slate-400">Make/Model</th>
                              <th className="text-left px-3 py-2 text-slate-400">Color</th>
                              <th className="text-left px-3 py-2 text-slate-400">Owner</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportDetail.vehicles.map((veh, idx) => (
                              <tr key={veh.id || idx} className="border-t border-slate-700">
                                <td className="px-3 py-2 text-white font-mono">{veh.license_plate || 'N/A'}</td>
                                <td className="px-3 py-2 text-slate-300">{`${veh.year || ''} ${veh.make || ''} ${veh.model || ''}`.trim() || 'N/A'}</td>
                                <td className="px-3 py-2 text-slate-300">{veh.color || 'N/A'}</td>
                                <td className="px-3 py-2 text-slate-300">{veh.registered_owner || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </DetailSection>
                  )}

                  {/* Locations */}
                  {reportDetail.locations && reportDetail.locations.length > 0 && (
                    <DetailSection title="LOCATIONS" icon={MapPin} count={reportDetail.locations.length}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {reportDetail.locations.map((loc, idx) => (
                          <div key={loc.id || idx} className="bg-slate-800/30 rounded-lg p-3 border border-slate-700">
                            <div className="flex items-center justify-between mb-1">
                              <h5 className="text-white font-medium">{loc.name || 'Location'}</h5>
                              <Badge variant="outline" className="text-xs">{loc.location_type?.replace(/_/g, ' ') || 'OTHER'}</Badge>
                            </div>
                            <p className="text-slate-400 text-sm">
                              {[loc.address, loc.city, loc.state].filter(Boolean).join(', ') || 'No address'}
                            </p>
                            {loc.latitude && loc.longitude && (
                              <p className="text-slate-500 text-xs mt-1">GPS: {loc.latitude}, {loc.longitude}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </DetailSection>
                  )}

                  {/* Evidence */}
                  {reportDetail.evidence && reportDetail.evidence.length > 0 && (
                    <DetailSection title="EVIDENCE INDEX" icon={FileImage} count={reportDetail.evidence.length}>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-800/50">
                              <th className="text-left px-3 py-2 text-slate-400">Exhibit</th>
                              <th className="text-left px-3 py-2 text-slate-400">Type</th>
                              <th className="text-left px-3 py-2 text-slate-400">Description</th>
                              <th className="text-left px-3 py-2 text-slate-400">Collected By</th>
                              <th className="text-left px-3 py-2 text-slate-400">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportDetail.evidence.map((ev, idx) => (
                              <tr key={ev.id || idx} className="border-t border-slate-700">
                                <td className="px-3 py-2 text-white font-medium">Exhibit {String.fromCharCode(65 + idx)}</td>
                                <td className="px-3 py-2 text-slate-300">{ev.evidence_type || 'N/A'}</td>
                                <td className="px-3 py-2 text-slate-300">{ev.description?.substring(0, 50) || 'N/A'}{ev.description?.length > 50 ? '...' : ''}</td>
                                <td className="px-3 py-2 text-slate-300">{ev.uploaded_by_name || 'N/A'}</td>
                                <td className="px-3 py-2 text-slate-300">{ev.uploaded_at ? new Date(ev.uploaded_at).toLocaleDateString() : 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </DetailSection>
                  )}

                  {/* Interviews */}
                  {reportDetail.interviews && reportDetail.interviews.length > 0 && (
                    <DetailSection title="INTERVIEWS CONDUCTED" icon={MessageSquare} count={reportDetail.interviews.length}>
                      <div className="space-y-3">
                        {reportDetail.interviews.map((intv, idx) => (
                          <div key={intv.id || idx} className="bg-slate-800/30 rounded-lg p-4 border border-slate-700">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="text-white font-medium">{intv.interviewee_name || 'Unknown'}</h5>
                              <span className="text-slate-400 text-sm">{intv.interview_date || 'No date'}</span>
                            </div>
                            {intv.relationship_to_case && (
                              <p className="text-slate-400 text-sm mb-1">Relationship: {intv.relationship_to_case}</p>
                            )}
                            {intv.summary && (
                              <p className="text-slate-300 text-sm">{intv.summary}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </DetailSection>
                  )}

                  {/* Signature Section */}
                  <DetailSection title="CERTIFICATION" icon={Scale}>
                    <div className="bg-slate-800/30 rounded-lg p-4">
                      <p className="text-slate-400 text-sm mb-4">
                        I certify that the information contained in this report is true and accurate to the best of my knowledge.
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-slate-500 text-xs">Investigator</p>
                          <p className="text-white">{reportDetail.investigator_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Date</p>
                          <p className="text-white">{reportDetail.signature_date || (reportDetail.created_at ? new Date(reportDetail.created_at).toLocaleDateString() : 'N/A')}</p>
                        </div>
                        {reportDetail.signature && (
                          <div className="col-span-2">
                            <p className="text-slate-500 text-xs">Digital Signature</p>
                            <p className="text-white italic">{reportDetail.signature}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </DetailSection>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-700 print:hidden">
                    {reportDetail.status === 'DRAFT' && (
                      <Button onClick={() => handleSubmit(reportDetail.id)} className="bg-blue-600 hover:bg-blue-700">
                        <Send className="w-4 h-4 mr-2" /> Submit for Approval
                      </Button>
                    )}
                    {reportDetail.status === 'SUBMITTED' && isAdmin && (
                      <Button onClick={() => handleApprove(reportDetail.id)} className="bg-green-600 hover:bg-green-700">
                        <CheckCircle className="w-4 h-4 mr-2" /> Approve Report
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => { setViewReport(null); setReportDetail(null); }} className="border-slate-600">
                      Close
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">Failed to load report details</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
